import React from "react";
import Head from "next/head";
import Link from "next/link";
import { FrazzleLogo } from "@/funnel/icons";
import JsonLd from "@/components/JsonLd";
import { SITE_URL, softwareApplicationLd } from "@/lib/structuredData";

/** Marketing-site chrome (www.frazzlecrm.com): nav + footer. */

export const APP_URL = "https://app.frazzlecrm.com";
const SITE_HOME = process.env.NODE_ENV === "development" ? "/site" : "/";

const DEFAULT_DESCRIPTION =
  "FrazzleCRM — the CRM where the funnel map IS the automation. Map it. Automate it.";

/**
 * Social cards live here, in ONE place: next/head only de-duplicates `meta`
 * by `name`, never by `property`, so a page emitting its own `og:*` tags on
 * top of the shell's would ship both to the crawler. Pages pass overrides
 * as props instead.
 */
export default function SiteShell({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogTitle,
  ogType = "website",
  ogImage = `${SITE_URL}/og-share.png`,
  ogImageAlt = "FrazzleCRM — the CRM where the funnel map is the automation",
  children,
}: {
  title: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogType?: "website" | "article";
  /** 1200x630 card. Template pages pass their generated map card. */
  ogImage?: string;
  ogImageAlt?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-white text-slate-800">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        {canonical && <link rel="canonical" href={canonical} />}
        <meta property="og:site_name" content="FrazzleCRM" />
        <meta property="og:type" content={ogType} />
        <meta property="og:title" content={ogTitle ?? title} />
        <meta property="og:description" content={description} />
        {canonical && <meta property="og:url" content={canonical} />}
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={ogImageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle ?? title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Head>
      {/* Site-wide product schema (SoftwareApplication + pricing) on every
          marketing page — the blob AI answer engines cite for "best CRM for
          X" / "how much is FrazzleCRM". */}
      <JsonLd data={softwareApplicationLd()} />

      <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3.5">
          <Link href={SITE_HOME} className="flex items-center gap-2">
            <FrazzleLogo size={30} />
            <span className="text-[17px] font-bold tracking-tight">
              FrazzleCRM
            </span>
          </Link>
          <nav className="ml-4 hidden items-center gap-5 text-[13.5px] font-medium text-slate-600 sm:flex">
            {/* Measured on the live header before adding this: at 768px the
                row has ~42px of slack and at 640px it already wraps to two
                lines, so the offer pill only appears from lg (~226px slack).
                Every narrower viewport reaches the offer through the footer
                link, the hub banners and the /pricing + /vs callouts. */}
            <Link
              href="/founding-members"
              className="hidden rounded-full bg-amber-100 px-2.5 py-1 text-[12.5px] font-extrabold text-amber-900 hover:bg-amber-200 lg:inline-block"
            >
              $397 once
            </Link>
            <Link href="/templates" className="hover:text-slate-900">
              Templates
            </Link>
            <Link href="/tools" className="hover:text-slate-900">
              Free tools
            </Link>
            <Link href="/pricing" className="hover:text-slate-900">
              Pricing
            </Link>
            <Link href="/ai" className="hover:text-slate-900">
              AI &amp; MCP
            </Link>
            <Link href="/blog" className="hover:text-slate-900">
              Blog
            </Link>
            {/* Sixth item only where there is room — below lg the header is
                already tight, and the footer link carries it everywhere. */}
            <Link href="/learn" className="hidden hover:text-slate-900 lg:inline">
              Glossary
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-2.5">
            <a
              href={`${APP_URL}/login`}
              className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-100"
            >
              Sign in
            </a>
            <a
              href={`${APP_URL}/signup`}
              className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-indigo-500"
            >
              Start free
            </a>
          </div>
        </div>
      </header>

      {children}

      <footer className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-6 py-8 text-[12.5px] text-slate-500">
          <span className="flex items-center gap-2 font-semibold text-slate-700">
            <FrazzleLogo size={20} /> FrazzleCRM
          </span>
          <span className="text-slate-300">·</span>
          {/* Below sm the nav is hidden entirely, so the footer is the only
              path to the offer on a phone — it goes first. */}
          <Link
            href="/founding-members"
            className="font-bold text-amber-800 hover:text-amber-900"
          >
            Founding Member offer
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/templates" className="hover:text-slate-700">
            Templates
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/tools" className="hover:text-slate-700">
            Free tools
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/integrations" className="hover:text-slate-700">
            Integrations
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/embed" className="hover:text-slate-700">
            Embed a map
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/sales-scripts" className="hover:text-slate-700">
            Sales Scripts
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/ai" className="hover:text-slate-700">
            AI &amp; MCP
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/blog" className="hover:text-slate-700">
            Blog
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/learn" className="hover:text-slate-700">
            Glossary
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/building-in-public" className="hover:text-slate-700">
            Building in public
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/changelog" className="hover:text-slate-700">
            Changelog
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/vs/gohighlevel" className="hover:text-slate-700">
            vs GoHighLevel
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/vs/activecampaign" className="hover:text-slate-700">
            vs ActiveCampaign
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/vs/make" className="hover:text-slate-700">
            vs Make
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/pricing" className="hover:text-slate-700">
            Pricing
          </Link>
          <span className="text-slate-300">·</span>
          <a href={`${APP_URL}/login`} className="hover:text-slate-700">
            Sign in
          </a>
          <span className="text-slate-300">·</span>
          <Link href="/docs" className="hover:text-slate-700">
            API docs
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/terms" className="hover:text-slate-700">
            Terms
          </Link>
          <span className="text-slate-300">·</span>
          <Link href="/privacy" className="hover:text-slate-700">
            Privacy
          </Link>
          <span className="text-slate-300">·</span>
          <a
            href="mailto:support@frazzlecrm.com"
            className="hover:text-slate-700"
          >
            support@frazzlecrm.com
          </a>
          <span className="ml-auto text-slate-400">
            © {new Date().getFullYear()} FrazzleCRM. Don&apos;t get Frazzled… Get FrazzleCRM.
          </span>
        </div>
      </footer>
    </div>
  );
}
